import React, { useState } from 'react';
import {
  useTranslation,
  DocumentAlertReviewModal,
  convertToAnyFormat,
  FormikHelpers, message,
  toSnakeCaseNested,
  useDownloadFile,
  CheckboxChangeEvent,
  documentService,
  Button,
  eventBus,
  formatCheckList,
  initialCheckList,
  trackHotjarEvent,
} from '@lib/index';
import {
  DocumentAlertParams, ReviewAlertFormValues, ReviewDocumentProps,
} from './types';
import { documentReviewModalInitialValues } from './schema/DocTabDefaultValue';
import { documentReviewFormValidation } from './schema/DocTabFormValidation';

const ReviewDocument = ({
  formattedWorkerRut,
  workerRutHeader,
  formattedContractorRut,
  contractorRutHeader,
  file,
  worker,
  stencil,
  disabled,
}: ReviewDocumentProps & {
  formattedContractorRut: string;
  contractorRutHeader: string;
}) => {
  const { t } = useTranslation();
  const [downloadFile] = useDownloadFile();
  const [indefiniteTerm, setIndefiniteTerm] = useState(file?.indefiniteTerm);
  const handleIndefiniteTerm = (e: CheckboxChangeEvent) => setIndefiniteTerm(e.target.checked);
  const { useUpdateDocumentMutation } = documentService;
  const [updateDocument] = useUpdateDocumentMutation();
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const fileExpirationDate = file?.expiresAt ? convertToAnyFormat(file?.expiresAt, 'DD/MM/YYYY') : '';
  const employeeName = worker?.name;
  const isRegularPeriodicity = stencil?.periodicity !== 'monthly';
  const downloadParams = {
    hasHost: true,
    url: file?.resourceUrl,
    filename: file?.name,
  };
  const reviewInitialValues = {
    ...documentReviewModalInitialValues,
    id: file?.id,
    checklist: initialCheckList(file?.checklist ?? []),
    revisionComment: file?.revisionComment,
    expiresAt: file?.expiresAt,
    indefiniteTerm: file?.indefiniteTerm,
  };
  const onSubmit = async (
    values: ReviewAlertFormValues,
    {
      setSubmitting,
      resetForm,
    }: FormikHelpers<ReviewAlertFormValues>,
  ) => {
    trackHotjarEvent(values.approved ? 'document_review_approved_submitted' : 'document_review_rejected_submitted');
    try {
      const holderId = file?.holderId || file?.contractorWorkerId;
      const holderType = file?.holderType || (file?.contractorWorkerId ? 'ContractorWorker' : undefined);
      const expirationDateRequired = indefiniteTerm || stencil?.periodicity === 'monthly'
        ? { expiresAt: null } : {};
      const formValues = toSnakeCaseNested({
        ...values,
        ...expirationDateRequired,
        checklist: formatCheckList(stencil?.docStencilChecklists, values.checklist) || [],
        holderId,
        holderType,
        indefiniteTerm,
      }) as DocumentAlertParams;
      await updateDocument(formValues);
      eventBus.dispatch('document-updated', {
        holderId: holderId ? Number(holderId) : undefined,
      });
      message.success(t('alert.revisionSuccess', { document: stencil?.name }), 2);
      const finalExpiresAt = indefiniteTerm || stencil?.periodicity === 'monthly' ? null : values.expiresAt;
      eventBus.dispatch('document-updated-optimistic', {
        id: file?.id,
        status: formValues.approved ? 'approved' : 'rejected',
        approved: formValues.approved,
        revisionComment: values.revisionComment,
        expiresAt: finalExpiresAt,
        indefiniteTerm,
      });
      setSubmitting(false);
      resetForm();
      setIsReviewModalVisible(false);
      trackHotjarEvent(formValues.approved ? 'document_review_approved_success' : 'document_review_rejected_success');
    } catch {
      message.error(t('alert.revisionError', { document: stencil?.name }), 2);
      trackHotjarEvent(values.approved ? 'document_review_approved_failed' : 'document_review_rejected_failed');
    }
  };

  const handleOpenModal = () => {
    eventBus.dispatch('handleClosePopOver', {});
    setIsReviewModalVisible(true);
    trackHotjarEvent('document_review_opened');
  };

  return (
    <>
      <DocumentAlertReviewModal
        reviewModalInitialValues={reviewInitialValues}
        reviewModalFormValidation={documentReviewFormValidation}
        handleDownloadFile={downloadFile}
        showExpirationDate={isRegularPeriodicity}
        indefiniteTerm={indefiniteTerm}
        handleIndefiniteTerm={handleIndefiniteTerm}
        setIsReviewModalVisible={setIsReviewModalVisible}
        isReviewModalVisible={isReviewModalVisible}
        expirationDate={fileExpirationDate}
        employeeName={employeeName as string}
        employeeStatus={worker?.status}
        downloadParams={downloadParams}
        documentName={stencil?.name}
        rut={formattedWorkerRut}
        rutHeader={workerRutHeader}
        plate={worker?.plates}
        code={worker?.code}
        category={worker?.category}
        expirable={stencil?.expirable}
        periodicity={stencil?.periodicity}
        expirationDays={stencil?.expirationDays}
        docStencilCheckList={stencil?.docStencilChecklists}
        resourceUrl={file?.resourceUrl}
        contractorRut={formattedContractorRut}
        contractorRutHeader={contractorRutHeader}
        contractorName={file?.contractor?.contractorName}
        onSubmit={onSubmit}
        period={file?.month}
        loadComment={file?.loadComment}
      />
      <Button onClick={handleOpenModal} type="link" disabled={disabled}>
        {t('utils.sendDocumentation.review')}
      </Button>
    </>
  );
};

export default ReviewDocument;
